import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import type * as React from 'react';
import type { DocumentDetailPayload } from '@Application/Beneficiaries/Documents/Actions/documentFetched';
import documentExported, {
	type DocumentExportedAction,
} from '@Application/Beneficiaries/Documents/Actions/documentExported';
import documentExportFailed, {
	type DocumentExportFailedAction,
} from '@Application/Beneficiaries/Documents/Actions/documentExportFailed';
import buildDocumentExportData from '@Application/Beneficiaries/Documents/buildDocumentExportData';
import buildDocumentExportFilename from '@Application/Beneficiaries/Documents/buildDocumentExportFilename';
import DOCUMENT_RENDER_OPTIONS from '@Application/Beneficiaries/Documents/documentRenderOptions';
import type ExportDocument from '@Application/Beneficiaries/Documents/Commands/ExportDocument';
import ensureDocxKitModules from '@Application/Documents/DocxKit/ensureDocxKitModules';
import { getFile, post } from '@Domain/Api';
import { downloadBlob } from '@Infra/Download';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import { renderTemplate } from '@verclam/docx-kit-template-engine';

type Actions = DocumentExportedAction | DocumentExportFailedAction;

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** The only two `renderTemplate` issue codes that make the produced bytes unusable — an unreadable
 * package, or a template stripped of its catalog (the library then hands back the input bytes
 * unchanged). Every other issue stays unsurfaced: rendering always produces a document, and a field
 * left blank because it was never addressed is the intended outcome, not a failure. */
const FATAL_RENDER_ISSUE_CODES: readonly string[] = [
	'template.package.unreadable',
	'template.catalog.absent',
];

/**
 * The export is traced together with the content it renders, never ahead of it: the template file is
 * read first, so a permission or tenant failure there never records a trace for a download that could
 * not even start.
 */
export default (command: ExportDocument) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const baseUrl = `${buildSubjectApiPath(command.facilityId, command.subject)}/documents/${command.documentId}`;

			await ensureDocxKitModules();

			const { blob: templateBlob } = await getFile(`${baseUrl}/template-file`);
			const payload = await post<DocumentDetailPayload>(`${baseUrl}/exports`, {});

			const templateBytes = new Uint8Array(await templateBlob.arrayBuffer());
			const { bytes, report } = renderTemplate(
				templateBytes,
				buildDocumentExportData(payload),
				DOCUMENT_RENDER_OPTIONS,
			);

			if (report.issues.some((issue) => FATAL_RENDER_ISSUE_CODES.includes(issue.code))) {
				const action = documentExportFailed({
					message: "Le gabarit de ce document est illisible : le fichier n'a pas pu être assemblé.",
					fieldErrors: [],
				});
				dispatch(action);
				return Promise.reject(action);
			}

			// `new Blob([bytes])` does not compile: `BlobPart` does not accept `Uint8Array<ArrayBufferLike>`.
			// `bytes.slice()` copies into a plain `ArrayBuffer`-backed view, which does.
			downloadBlob(
				new Blob([bytes.slice()], { type: DOCX_MIME }),
				buildDocumentExportFilename(payload.title),
			);

			const action = documentExported({ documentId: command.documentId });
			dispatch(action);
			return action;
		} catch (error) {
			const action = documentExportFailed(
				extractMessages(error) ?? {
					message: "Le document n'a pas pu être téléchargé.",
					fieldErrors: [],
				},
			);
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
